import "./styles.scss";

import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import classnames from "classnames";

import { useApp } from "context/app";
import { useNotifications } from "context/notifications";

import DetailsCard from "components/DetailsCard";
import Button from "components/Button";
import CheckButton from "components/CheckButton";
import SelectablePill from "components/SelectablePill";
import { WebsiteInfoModal } from "page_components/shared/tasks/modals";

import { ReactComponent as ArrowIcon } from "assets/icons/new/forward-arrow.svg";
import { ReactComponent as NotificationIcon } from "assets/icons/menu/notification.svg";

import useResponsive from "libs/helpers/useResponsive";
import notification_render, {
  getNotificationTitle,
  isNewTaskNotification,
} from "libs/notification_render";

const mainClass = "dashboard-notifications";

const Notifications = () => {
  const { max } = useResponsive();
  const { user } = useApp();

  const { t } = useTranslation();
  const { allData, readHandler } = useNotifications();

  const [websiteInfoId, setWebsiteInfoId] = useState(null);

  const unreadNotifications = allData.filter((item) => item?.status === 0);
  const notifications = unreadNotifications.slice(0, 35);

  return (
    <>
      <DetailsCard
        title={t("Notifications")}
        description={t("Information about your website, tasks, and more.")}
        action={
          !max.sm
            ? {
                label: t("See all"),
                href:
                  unreadNotifications.length === 0
                    ? "/notifications?show_read=true"
                    : "/notifications",
              }
            : undefined
        }
      >
        {max.sm ? (
          <div className={`${mainClass}__mobile`}>
            <div
              className={classnames(`${mainClass}__mobile__icon`, {
                [`${mainClass}__mobile__icon--empty`]:
                  unreadNotifications.length === 0,
              })}
            >
              <NotificationIcon />
            </div>
            <p>
              {t(
                unreadNotifications.length === 0
                  ? "You are all caught up."
                  : "Unread notifications dashboard message",
              )}
            </p>
            <strong>
              {t("Unread notifications count", {
                count: unreadNotifications.length,
              })}
            </strong>
            <Button
              href={
                unreadNotifications.length === 0
                  ? "/notifications?show_read=true"
                  : "/notifications"
              }
            >
              {t("See all")}
            </Button>
          </div>
        ) : (
          <div
            className={classnames(mainClass, {
              [`${mainClass}--empty`]: notifications.length === 0,
            })}
          >
            {notifications.length === 0 ? (
              <div className={`${mainClass}__empty`}>
                <div className={`${mainClass}__empty__icon`}>
                  <NotificationIcon />
                </div>
                <p>{t("You are all caught up.")}</p>
              </div>
            ) : (
              notifications.map((item) => {
                const notification = notification_render(
                  item?.template,
                  item?.context,
                  t,
                );

                const newWebsiteComment =
                  item?.template === null || item?.template === 24;
                const taskAddedToUser = item?.template === 21;
                const hasLink =
                  (!!item?.href &&
                    item?.href.indexOf("undefined") === -1 &&
                    (newWebsiteComment
                      ? user?.type === "client"
                        ? !!item?.website?._id
                        : false
                      : true)) ||
                  taskAddedToUser;
                const hasAction =
                  newWebsiteComment &&
                  user?.type !== "client" &&
                  !!item?.website?._id;
                const Tag = hasAction ? "div" : hasLink ? Link : "div";

                return (
                  <Tag
                    key={item?._id}
                    to={
                      hasAction
                        ? undefined
                        : hasLink
                          ? newWebsiteComment
                            ? `/websites/${item?.website?._id}`
                            : taskAddedToUser
                              ? item.href.replace("websites", "tasks")
                              : item.href
                          : undefined
                    }
                    className={`${mainClass}__item`}
                    onClick={
                      !hasAction ? () => readHandler(item?._id, 1) : undefined
                    }
                  >
                    <div className={`${mainClass}__item__content`}>
                      {isNewTaskNotification(item?.template) ? (
                        <span>{getNotificationTitle(item, t)}</span>
                      ) : (
                        <span
                          dangerouslySetInnerHTML={{
                            __html: !!item?.template
                              ? notification?.text
                              : item?.custom_text,
                          }}
                        />
                      )}
                    </div>
                    <div className={`${mainClass}__item__category`}>
                      {item?.template && (
                        <SelectablePill
                          data={{
                            label: t(
                              item?.template === 17 ||
                                item?.template === 18 ||
                                item?.template === 19 ||
                                item?.template === 20 ||
                                taskAddedToUser
                                ? "Task"
                                : item?.template === 28
                                  ? "Registration"
                                  : "Website [SHORT]",
                            ),
                            color:
                              item?.template === 17 ||
                              item?.template === 18 ||
                              item?.template === 19 ||
                              item?.template === 20 ||
                              taskAddedToUser
                                ? "yellow"
                                : item?.template === 28
                                  ? "blue"
                                  : "green",
                          }}
                        />
                      )}
                    </div>
                    <CheckButton
                      checked={item?.status === 1}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        readHandler(item?._id, item?.status === 1 ? 0 : 1);
                      }}
                    />
                    <div className={`${mainClass}__item__arrow`}>
                      {(hasAction || hasLink) && <ArrowIcon />}
                    </div>
                    {hasAction && (
                      <button
                        className={`${mainClass}__item__action`}
                        type="button"
                        onClick={() => {
                          setWebsiteInfoId(item.website._id);
                          readHandler(item?._id, 1);
                        }}
                      />
                    )}
                  </Tag>
                );
              })
            )}
          </div>
        )}
      </DetailsCard>

      <WebsiteInfoModal
        id={websiteInfoId}
        isOpen={!!websiteInfoId}
        onClose={() => setWebsiteInfoId(null)}
      />
    </>
  );
};

export default Notifications;
